import { inject } from '@angular/core'
import {
  ActivatedRouteSnapshot,
  createUrlTreeFromSnapshot,
  RedirectCommand,
  ResolveFn,
  UrlMatcher,
  UrlSegment,
} from '@angular/router'
import { HOME_SLUG, Language } from '@pai/commons'
import { catchError, map, Observable, of } from 'rxjs'

import { CURRENT_LANG } from 'app/core/i18n/language.token'
import { RoutePath } from 'app/static/route-path.enum'

export function getPathFromRouteUrl(url: UrlSegment[]): string {
  return url
    .map((segment) => segment.path)
    .filter((v) => !!v)
    .join('/')
}
export function nestedPathMatcher(path: `${string}/:${string}`): UrlMatcher {
  const [prefixPathPart, paramName] = path.split('/:')

  return (url: UrlSegment[]) => {
    return url.length > 1 && url[0].path === prefixPathPart
      ? {
          consumed: url,
          posParams: {
            [paramName]: new UrlSegment(getPathFromRouteUrl(url.slice(1)), {}),
          },
        }
      : null
  }
}

export function resolveWithRedirectToHomeWhenNull<T>(
  fn: (route: ActivatedRouteSnapshot) => Observable<T | null>,
): ResolveFn<T> {
  return (route: ActivatedRouteSnapshot): Observable<T | RedirectCommand> => {
    const lang = inject(CURRENT_LANG)

    // if route is empty - which is the home route - and the site is null, we redirect to /lang/server-error (without url update)

    return fn(route).pipe(
      map((site: T | null | RedirectCommand) => {
        return (
          site ??
          (route.url.length === 0
            ? new RedirectCommand(
                createUrlTreeFromSnapshot(route, ['/', lang, RoutePath.__SERVER_ERROR]),
                { info: 'home page not fetchable', skipLocationChange: true },
                //
              )
            : new RedirectCommand(
                createUrlTreeFromSnapshot(route, ['/', lang, HOME_SLUG]),
                { info: 'redirect to home page' },
                //
              ))
          //
        )
      }),
      catchError((error) =>
        of(
          new RedirectCommand(
            createUrlTreeFromSnapshot(route, ['/', lang, RoutePath.__SERVER_ERROR]),
            {
              info: 'error during data fetching for route, redirecting to server-error',
              state: { error },
              skipLocationChange: true,
            },
            //
          ),
        ),
      ),
    )
  }
}

export function resolveWithRedirectWhenNull<T>(
  fn: (route: ActivatedRouteSnapshot) => Observable<T | null>,
  redirectTo: (lang: Language) => string[],
): ResolveFn<T> {
  return (route: ActivatedRouteSnapshot): Observable<T | RedirectCommand> => {
    const lang = inject(CURRENT_LANG)
    return fn(route).pipe(
      map((site: T | null | RedirectCommand) =>
        site ? site : new RedirectCommand(createUrlTreeFromSnapshot(route, redirectTo(lang))),
      ),
    )
  }
}

export function resolveBySlug<T>(
  fn: (slug: string) => Observable<T | null>,
): (ars: ActivatedRouteSnapshot) => Observable<T | null> {
  // todo: catch errors and redirect to an error page
  return (route: ActivatedRouteSnapshot): Observable<T | null> => fn(route.params['slug'])
}
